import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../App';
import { useOcLabAccess } from '../components/OrganoidRequest/OcLabAccessContext';
import { createSessionOcApi } from './ocRequestApi';
import { isLabViewer } from './userAccountRole';

/**
 * Shared workspace for OC detail/stage pages (session or lab magic link).
 *
 * @returns {{
 *   mode: 'session'|'labPublic',
 *   api: Object|null,
 *   requestId: string|number|null,
 *   basePath: string,
 *   showInboxLink: boolean,
 *   labAccess: Object|null,
 *   authToken: string|null,
 *   readOnly: boolean,
 * }}
 */
export function useOcRequestWorkspace() {
  const labAccess = useOcLabAccess();
  const { id } = useParams();
  const context = useContext(AppContext) || {};
  const { token } = context;
  const readOnly = isLabViewer(context.userDetails, context.isSupplier);

  const sessionApi = useMemo(() => {
    if (labAccess?.labToken || !token || !id) return null;
    return createSessionOcApi(token, id);
  }, [labAccess?.labToken, token, id]);

  if (labAccess?.labToken) {
    return {
      mode: 'labPublic',
      api: labAccess.api,
      requestId: labAccess.request?.id ?? null,
      basePath: labAccess.basePath,
      showInboxLink: false,
      labAccess,
      authToken: null,
      readOnly: false,
    };
  }

  return {
    mode: 'session',
    api: sessionApi,
    requestId: id ?? null,
    basePath: id ? `/organoid-requests/${id}` : '/organoid-requests',
    showInboxLink: true,
    labAccess: null,
    authToken: token || null,
    readOnly,
  };
}
